import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { isPanelRequired } from '../api/client';
import { useApi } from '../api/context';
import type { MerchantPayment, PaymentState } from '../api/types';
import { DateRangeForm } from '../components/DateRangeForm';
import { useToast } from '../components/Toasts';
import {
  ErrorNotice,
  Loading,
  LockIcon,
  PageHeader,
  PaymentStateChip,
  Upsell,
} from '../components/ui';
import { useI18n } from '../i18n';
import { formatDateTime, formatDay, skopjeToday } from '../lib/dates';
import { saveBlob } from '../lib/download';
import { errorMessageKey } from '../lib/errors';
import { formatDen } from '../lib/money';
import {
  checkRange,
  PAYMENTS_MAX_DAYS,
  presetRange,
  toApiRange,
  type DayRangeValue,
  type PeriodPreset,
} from '../lib/ranges';
import { useDebounced, useLoad } from '../lib/useLoad';
import { useLiveEvents } from '../live/live';
import { usePanelActive } from '../state/merchant';

const PRESETS: readonly PeriodPreset[] = ['today', 'week', 'month', 'lastMonth'];

/**
 * Payments that moved (or may still move) money: settled or in flight. Only these have a ZINQ
 * fee and a net amount; a failed / expired / rejected / cancelled one shows a dash, as in the
 * CSV export and on the reconciliation page.
 */
const MONEY_MOVES: ReadonlySet<PaymentState> = new Set([
  'SETTLED',
  'CREATED',
  'AWAITING_AUTHORIZATION',
  'AUTHORIZED',
  'SUBMITTED',
]);

/** ?from=&to= of the page URL when valid, else the last 7 days. */
function rangeFromParams(params: URLSearchParams, today: string): DayRangeValue {
  const from = params.get('from');
  const to = params.get('to');
  if (from && to) {
    const r = { from, to };
    if (!checkRange(r, PAYMENTS_MAX_DAYS, today)) return r;
  }
  return presetRange('week', today);
}

interface Page {
  items: MerchantPayment[];
  nextCursor: string | null;
}

/** All QR payments of a period, with totals and the CSV export (web panel). */
export function PaymentsPage() {
  const { t, locale } = useI18n();
  const api = useApi();
  const notify = useToast();
  const panelActive = usePanelActive();
  const today = skopjeToday();
  const [params, setParams] = useSearchParams();
  const range = rangeFromParams(params, today);
  const [more, setMore] = useState<Page | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportGated, setExportGated] = useState(false);

  const { state, reload } = useLoad(
    async (signal) => {
      const apiRange = toApiRange(range);
      const [summary, page] = await Promise.all([
        api.summary(apiRange, signal),
        api.payments(apiRange, undefined, signal),
      ]);
      return { summary, page };
    },
    [api, range.from, range.to],
  );

  useEffect(() => {
    setMore(null);
  }, [state.data]);

  useEffect(() => {
    setExportGated(false);
  }, [range.from, range.to, panelActive]);

  const refresh = useDebounced(() => reload({ quiet: true }), 250);
  useLiveEvents(['payment.received', 'qr_intent.updated', 'resync'], (type) => {
    if (type === 'resync' || range.to >= today) refresh();
  });

  const setRange = (r: DayRangeValue): void => {
    setParams({ from: r.from, to: r.to }, { replace: true });
  };

  const exportCsv = async (): Promise<void> => {
    setExporting(true);
    try {
      const file = await api.exportCsv(range.from, range.to, locale);
      saveBlob(file.blob, file.filename);
      notify(t('payments.exported', { file: file.filename }));
    } catch (e) {
      if (isPanelRequired(e)) setExportGated(true);
      else notify(t(errorMessageKey(e)), 'danger');
    } finally {
      setExporting(false);
    }
  };

  const loadMore = async (cursor: string): Promise<void> => {
    setLoadingMore(true);
    try {
      const page = await api.payments(toApiRange(range), cursor);
      setMore((m) => ({ items: [...(m?.items ?? []), ...page.items], nextCursor: page.nextCursor }));
    } catch (e) {
      notify(t(errorMessageKey(e)), 'danger');
    } finally {
      setLoadingMore(false);
    }
  };

  let body: ReactNode;
  if (state.status === 'loading') body = <Loading />;
  else if (state.status === 'error') {
    body = isPanelRequired(state.error) ? (
      <Upsell reason="history" />
    ) : (
      <ErrorNotice error={state.error} onRetry={() => reload()} />
    );
  } else {
    const { summary, page } = state.data;
    const items = [...page.items, ...(more?.items ?? [])];
    const cursor = more ? more.nextCursor : page.nextCursor;
    body = (
      <section className="card">
        <p className="totals" data-testid="payments-totals">
          {t('payments.totals', {
            count: summary.count,
            gross: formatDen(summary.grossMinor),
            fee: formatDen(summary.mdrMinor),
            net: formatDen(summary.netMinor),
          })}
        </p>
        {items.length === 0 ? (
          <p className="empty">{t('payments.empty')}</p>
        ) : (
          <div className="table-wrap">
            <table className="table" data-testid="payments-table">
              <caption className="visually-hidden">
                {t('payments.caption', { from: formatDay(range.from), to: formatDay(range.to) })}
              </caption>
              <thead>
                <tr>
                  <th scope="col">{t('payments.col.date')}</th>
                  <th scope="col">{t('payments.col.status')}</th>
                  <th scope="col">{t('payments.col.reference')}</th>
                  <th scope="col">{t('payments.col.payer')}</th>
                  <th scope="col" className="col-optional">
                    {t('payments.col.kind')}
                  </th>
                  <th scope="col" className="num">
                    {t('payments.col.amount')}
                  </th>
                  <th scope="col" className="num">
                    {t('payments.col.fee')}
                  </th>
                  <th scope="col" className="num">
                    {t('payments.col.net')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.paymentId} data-reference={p.reference} data-state={p.state}>
                    <td className="nowrap">{formatDateTime(p.settledAt ?? p.createdAt, locale)}</td>
                    <td>
                      <PaymentStateChip state={p.state} />
                    </td>
                    <td className="mono">{p.reference}</td>
                    <td>{p.payerName}</td>
                    <td className="col-optional nowrap">{t(`payments.kind.${p.kind}`)}</td>
                    <td className="num">{formatDen(p.amountMinor)}</td>
                    <td className="num muted">{MONEY_MOVES.has(p.state) ? formatDen(p.mdrMinor) : '—'}</td>
                    <td className="num strong">{MONEY_MOVES.has(p.state) ? formatDen(p.netMinor) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {cursor ? (
          <div className="load-more">
            <button
              type="button"
              className="btn btn--secondary"
              disabled={loadingMore}
              onClick={() => void loadMore(cursor)}
            >
              {loadingMore ? t('app.loading') : t('app.loadMore')}
            </button>
          </div>
        ) : null}
      </section>
    );
  }

  return (
    <>
      <PageHeader
        title={t('payments.title')}
        subtitle={t('payments.caption', { from: formatDay(range.from), to: formatDay(range.to) })}
        actions={
          <button
            type="button"
            className="btn btn--secondary"
            onClick={() => void exportCsv()}
            disabled={exporting}
            data-testid="export-csv"
          >
            {panelActive === false ? <LockIcon size={14} /> : null}
            {exporting ? t('payments.exporting') : t('payments.export')}
          </button>
        }
      />
      <DateRangeForm
        value={range}
        today={today}
        maxDays={PAYMENTS_MAX_DAYS}
        presets={PRESETS}
        panelActive={panelActive}
        onChange={setRange}
      />
      {exportGated ? <Upsell reason="export" compact /> : null}
      {body}
    </>
  );
}
